import { Transaction } from '../../types';

export interface TransactionProgressIndicatorProps {
  /** The transaction object to display progress for */
  transaction: Transaction;
  
  /** Visual layout variant */
  variant?: 'horizontal' | 'vertical';
  
  /** Component size */
  size?: 'small' | 'medium' | 'large';
  
  /** Whether to show step labels */
  showLabels?: boolean;
  
  /** Whether to show step icons */
  showIcons?: boolean;
  
  /** Additional CSS class names */
  className?: string;
  
  /** Optional callback when step is clicked (for future interactivity) */
  onStepClick?: (stepId: string, stepIndex: number) => void;
  
  /** Whether to show progress percentage */
  showPercentage?: boolean;
  
  /** Custom step definitions (for advanced use cases) */
  customSteps?: ProgressStep[];
}

export interface ProgressStep {
  /** Unique identifier for the step */
  id: string;
  
  /** Display label for the step */
  label: string;
  
  /** Short label for compact displays */
  shortLabel: string;
  
  /** Icon to display (emoji or icon name) */
  icon: string;
  
  /** Detailed description of the step */
  description: string;
  
  /** Transaction statuses that correspond to this step */
  statuses: string[];
  
  /** Current status of this step */
  status: StepStatus;
  
  /** Whether this step is optional */
  optional?: boolean;
  
  /** Custom styling for this step */
  className?: string;
}

export type StepStatus = 'completed' | 'current' | 'pending' | 'skipped' | 'error';

export interface ProgressState {
  /** Current active step number (1-based) */
  currentStep: number;
  
  /** Array of all progress steps with their statuses */
  steps: ProgressStep[];
  
  /** Number of completed steps */
  completedSteps: number;
  
  /** Progress percentage (0-100) */
  progressPercentage: number;
  
  /** Whether the transaction is in a disputed state */
  isDisputed?: boolean;
  
  /** Whether the transaction is cancelled */
  isCancelled?: boolean;
  
  /** Whether the transaction is in an error state */
  hasError?: boolean;
  
  /** Error message if applicable */
  errorMessage?: string;
}

export interface StepClickEvent {
  stepId: string;
  stepIndex: number;
  step: ProgressStep;
  transaction: Transaction;
}

// Default props and constants
export const DEFAULT_PROPS: Required<Pick<TransactionProgressIndicatorProps, 
  'variant' | 'size' | 'showLabels' | 'showIcons' | 'showPercentage'>> = {
  variant: 'horizontal',
  size: 'medium',
  showLabels: true,
  showIcons: true,
  showPercentage: false
};

export const STEP_SIZES = {
  small: {
    circleSize: 24,
    fontSize: 12,
    spacing: 8
  },
  medium: {
    circleSize: 32,
    fontSize: 14,
    spacing: 12
  },
  large: {
    circleSize: 40,
    fontSize: 16,
    spacing: 16
  }
} as const;

// Type guards for runtime validation
export function isValidTransaction(transaction: any): transaction is Transaction {
  return transaction && 
         typeof transaction.id === 'string' &&
         typeof transaction.status === 'string' &&
         typeof transaction.createdAt === 'string';
}

export function isValidProgressStep(step: any): step is ProgressStep {
  return step &&
         typeof step.id === 'string' &&
         typeof step.label === 'string' &&
         typeof step.icon === 'string' &&
         Array.isArray(step.statuses);
}

export function isValidStepStatus(status: any): status is StepStatus {
  return ['completed', 'current', 'pending', 'skipped', 'error'].includes(status);
}